import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import { Check } from "lucide-react";
import { useReducedMotion } from "@/utils/motion";

export type SunburstChannel = { label: string; icon: LucideIcon };

type Props = {
  channels: readonly SunburstChannel[];
  centerLabel: string;
  /** Short line shown in the centre once every ray is lit. */
  centerDone?: string;
  /** The outcome shown in the centre when the sweep completes. */
  booking?: { title: string; detail: string };
  /** Accessible summary of what the diagram shows. */
  ariaLabel: string;
  /**
   * Sweeps like a clock when omitted. Otherwise lights this many channels
   * (0–5): each channel lights its own sector of rays, filled in gradually.
   */
  litCount?: number;
  tone?: "light" | "navy";
  showLabels?: boolean;
  className?: string;
};

const C = 300;
const RAYS = 20;
const STEP_DEG = 360 / RAYS;
const CHANNEL_EVERY = 4;
const RING_R = 92;
const RAY_START = 116;
const RAY_LONG = 258;
const RAY_SHORT = 214;

/* Sweep timing (ms). */
const FILL_STEP = 150;
const DRAIN_STEP = 70;
const HOLD_FULL = 2800;
const HOLD_EMPTY = 500;
const FIRST_HOLD = 2400;
const FOLLOW_STEP = 45;

const rays = Array.from({ length: RAYS }, (_, i) => {
  const angle = -90 + i * STEP_DEG;
  const channel = i % CHANNEL_EVERY === 0 ? i / CHANNEL_EVERY : -1;
  const end = channel >= 0 || i % 2 === 0 ? RAY_LONG : RAY_SHORT;
  return { i, angle, channel, end };
});

/** Rays lit when `channels` channels are on: each channel owns its sector. */
function raysForChannels(channels: number) {
  return Math.max(0, Math.min(RAYS, channels * CHANNEL_EVERY));
}

/** The lit arc: rays with `start <= i < end` carry full light. */
type Arc = { start: number; end: number };

/**
 * Clock sweep. Light fills the rays one by one (the hand), holds on the full
 * sun, then drains in the same direction before the next pass. Unlit rays keep
 * their base colour, so no ray ever disappears.
 */
function useSweep(enabled: boolean, rootRef: React.RefObject<HTMLElement | null>): Arc {
  const [arc, setArc] = useState<Arc>({ start: 0, end: RAYS });
  const visibleRef = useRef(true);

  useEffect(() => {
    if (!enabled) {
      setArc({ start: 0, end: RAYS });
      return;
    }
    const el = rootRef.current;
    const io =
      el && "IntersectionObserver" in window
        ? new IntersectionObserver(([entry]) => {
            visibleRef.current = entry.isIntersecting;
          })
        : null;
    if (el && io) io.observe(el);

    let timer: number;
    let start = 0;
    let end = RAYS;
    const schedule = (ms: number) => {
      timer = window.setTimeout(tick, ms);
    };
    function tick() {
      if (!visibleRef.current || document.hidden) {
        schedule(800);
        return;
      }
      if (start === end) {
        start = 0;
        end = 1;
      } else if (end < RAYS) {
        end += 1;
      } else {
        start += 1;
      }
      setArc({ start, end });
      const full = start === 0 && end === RAYS;
      const empty = start === end;
      schedule(full ? HOLD_FULL : empty ? HOLD_EMPTY : start === 0 ? FILL_STEP : DRAIN_STEP);
    }
    schedule(FIRST_HOLD);
    return () => {
      window.clearTimeout(timer);
      io?.disconnect();
    };
  }, [enabled, rootRef]);

  return arc;
}

/** Walks the lit ray count towards `target` one ray at a time. */
function useFollow(target: number, instant: boolean) {
  const [lit, setLit] = useState(target);
  useEffect(() => {
    if (instant) {
      setLit(target);
      return;
    }
    const id = window.setInterval(() => {
      setLit((value) => {
        if (value === target) {
          window.clearInterval(id);
          return value;
        }
        return value + (target > value ? 1 : -1);
      });
    }, FOLLOW_STEP);
    return () => window.clearInterval(id);
  }, [target, instant]);
  return lit;
}

export function SunburstDiagram({
  channels,
  centerLabel,
  centerDone,
  booking,
  ariaLabel,
  litCount,
  tone = "light",
  showLabels = true,
  className = "",
}: Props) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const controlled = litCount !== undefined;
  const swept = useSweep(!controlled && !reduced, rootRef);
  const followed = useFollow(raysForChannels(litCount ?? 5), reduced);
  const arc: Arc = controlled ? { start: 0, end: followed } : swept;
  const isLit = (i: number) => i >= arc.start && i < arc.end;
  const litTotal = arc.end - arc.start;
  const complete = arc.start === 0 && arc.end >= RAYS;
  const filling = !controlled && arc.start === 0 && arc.end > 0 && arc.end < RAYS;
  const [hovered, setHovered] = useState<number | null>(null);
  const navy = tone === "navy";

  /* The step in focus: the one the hand is crossing, or the step in view. */
  const activeChannel = controlled
    ? Math.min(litCount ?? 0, 5) - 1
    : filling
      ? Math.floor((arc.end - 1) / CHANNEL_EVERY)
      : -1;
  const headRay = filling ? arc.end - 1 : -1;

  /* Every ray stays visible at a solid base; light brightens it. */
  const base = navy ? "rgb(114 181 177 / 0.42)" : "rgb(114 181 177 / 0.5)";
  const lit = navy ? "#8cc4c0" : "#72b5b1";
  const dots = navy ? "rgb(255 255 255 / 0.16)" : "rgb(26 60 94 / 0.14)";
  const showBooking = Boolean(booking) && complete;

  return (
    <div
      ref={rootRef}
      role="img"
      aria-label={ariaLabel}
      className={`relative aspect-square w-full select-none ${className}`}
    >
      {/* Light pool behind the mark; grows with the lit rays */}
      <div
        aria-hidden="true"
        className="absolute inset-[8%] rounded-full transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle, rgb(114 181 177 / ${navy ? 0.34 : 0.3}) 0%, rgb(114 181 177 / 0.08) 45%, transparent 70%)`,
          opacity: 0.45 + (litTotal / RAYS) * 0.55,
        }}
      />

      <svg viewBox="0 0 600 600" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
        <defs>
          {/* User-space units: a vertical line has a zero-width bounding box,
              which would make an objectBoundingBox gradient paint nothing. */}
          <linearGradient
            id={`lit-${uid}`}
            gradientUnits="userSpaceOnUse"
            x1={C}
            y1={C - RAY_START}
            x2={C}
            y2={C - RAY_LONG}
          >
            <stop offset="0" stopColor="#72b5b1" />
            <stop offset="1" stopColor={navy ? "#a9d3d0" : "#4f8f8b"} />
          </linearGradient>
          <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer orbit of faint dots, turning slowly */}
        <g className="spin-slow" style={{ transformOrigin: "300px 300px" }}>
          {Array.from({ length: 60 }, (_, k) => {
            const rad = (k * 6 * Math.PI) / 180;
            return (
              <circle
                key={k}
                cx={C + 286 * Math.cos(rad)}
                cy={C + 286 * Math.sin(rad)}
                r={k % 5 === 0 ? 2.2 : 1.2}
                fill={dots}
              />
            );
          })}
        </g>

        {rays.map(({ i, angle, channel, end }) => {
          const on = isLit(i);
          const isChannel = channel >= 0;
          const emphasised = isChannel && (channel === activeChannel || channel === hovered);
          const stroke = !on ? base : isChannel ? `url(#lit-${uid})` : lit;
          return (
            <g key={i} transform={`rotate(${angle + 90} ${C} ${C})`}>
              <line
                x1={C}
                y1={C - RAY_START}
                x2={C}
                y2={C - end}
                stroke={stroke}
                strokeWidth={emphasised ? 24 : isChannel ? 17 : i === headRay ? 16 : 13}
                strokeLinecap="round"
                filter={emphasised || i === headRay ? `url(#glow-${uid})` : undefined}
                style={{ transition: "stroke 0.5s ease, stroke-width 0.4s ease" }}
              />
              {isChannel ? (
                /* A node lights at the tip of each step's ray */
                <circle
                  cx={C}
                  cy={C - end}
                  r={emphasised ? 9 : 7}
                  fill={navy ? "#122c46" : "#ffffff"}
                  stroke="#72b5b1"
                  strokeWidth={3}
                  style={{ opacity: on ? 1 : 0, transition: "opacity 0.4s ease, r 0.4s ease" }}
                />
              ) : null}
            </g>
          );
        })}

        {/* Ring and centre */}
        <circle
          cx={C}
          cy={C}
          r={RING_R}
          fill="none"
          stroke={complete ? "#72b5b1" : navy ? "rgb(114 181 177 / 0.45)" : "rgb(114 181 177 / 0.55)"}
          strokeWidth={complete ? 3 : 2}
          style={{ transition: "stroke 0.6s ease" }}
        />
        {complete && !reduced ? (
          <circle cx={C} cy={C} r={RING_R} fill="none" stroke="#72b5b1" strokeWidth={2} className="pulse-ring" />
        ) : null}
        <circle cx={C} cy={C} r={RING_R - 12} fill={navy ? "#122c46" : "#ffffff"} />
      </svg>

      {/* Centre: the business, and the booking it receives when every channel is lit */}
      <div className="absolute left-1/2 top-1/2 grid w-[26%] -translate-x-1/2 -translate-y-1/2 place-items-center text-center">
        <div
          className="col-start-1 row-start-1 flex flex-col items-center transition-opacity duration-500"
          style={{ opacity: showBooking ? 0 : 1 }}
        >
          <span
            className={`font-display text-[clamp(0.95rem,2.4vw,1.35rem)] font-semibold leading-tight ${navy ? "text-white" : "text-brand-primary"}`}
          >
            {centerLabel}
          </span>
          {centerDone && !booking ? (
            <span
              className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold transition-opacity duration-500 ${navy ? "text-teal-light" : "text-teal-strong"}`}
              style={{ opacity: complete ? 1 : 0 }}
            >
              <Check className="size-3" aria-hidden="true" />
              {centerDone}
            </span>
          ) : null}
        </div>
        {booking ? (
          <div
            aria-hidden="true"
            className="col-start-1 row-start-1 flex flex-col items-center transition-[opacity,scale] duration-500 ease-[var(--ease-out-expo)]"
            style={{ opacity: showBooking ? 1 : 0, scale: showBooking ? "1" : "0.92" }}
          >
            <span className="grid size-7 place-items-center rounded-full bg-brand-secondary text-brand-primary shadow-[0_0_0_5px_rgb(114_181_177/0.2)] sm:size-8">
              <Check className="size-4" strokeWidth={3} aria-hidden="true" />
            </span>
            <span className="mt-1.5 text-xs font-semibold leading-tight text-brand-primary sm:text-sm">{booking.title}</span>
            <span className="mt-0.5 text-xs leading-tight text-ink-muted">{booking.detail}</span>
          </div>
        ) : null}
      </div>

      {/* Channel labels: off, on, and the one in focus (filled) */}
      {showLabels
        ? channels.map((channel, index) => {
            const angle = -90 + index * CHANNEL_EVERY * STEP_DEG;
            const rad = (angle * Math.PI) / 180;
            const r = 45.5;
            const on = isLit(index * CHANNEL_EVERY);
            const focus = index === activeChannel || index === hovered;
            const Icon = channel.icon;
            const style: CSSProperties = {
              left: `${50 + r * Math.cos(rad)}%`,
              top: `${50 + r * Math.sin(rad)}%`,
              transform: `translate(${-50 - 34 * Math.cos(rad)}%, -50%) scale(${focus ? 1.1 : 1})`,
            };
            const state = focus
              ? "border-brand-secondary bg-brand-secondary text-brand-primary shadow-[0_10px_28px_-10px_rgb(114_181_177/0.9)]"
              : on
                ? navy
                  ? "border-brand-secondary/60 bg-navy-deep text-white"
                  : "border-brand-secondary/70 bg-teal-soft text-brand-primary"
                : navy
                  ? "border-white/10 bg-navy-deep/80 text-on-navy-muted"
                  : "border-brand-neutral bg-white/85 text-ink-muted";
            const iconColor = focus
              ? "text-brand-primary"
              : on
                ? navy
                  ? "text-teal-light"
                  : "text-teal-strong"
                : navy
                  ? "text-white/40"
                  : "text-ink-muted/60";
            return (
              <span
                key={channel.label}
                aria-hidden="true"
                onPointerEnter={() => setHovered(index)}
                onPointerLeave={() => setHovered(null)}
                style={style}
                className={`absolute inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[clamp(0.66rem,1.55vw,0.82rem)] font-semibold shadow-lit transition-[background-color,border-color,color,transform,box-shadow] duration-500 sm:px-3 sm:py-1.5 ${state}`}
              >
                <Icon className={`size-3.5 shrink-0 transition-colors duration-500 ${iconColor}`} aria-hidden="true" />
                {channel.label}
              </span>
            );
          })
        : null}
    </div>
  );
}
